import React, { useEffect, useState } from 'react'
import {
  useTransactions,
  TransactionsRequest,
  TransactionListItem,
} from '../../../services/api/transactions'
import { EventListItem, useEvents } from '../../../services/api/events'
import { defaultPagination } from '../../../services/api/client/types'
import { View } from './View'
import { useQuery } from '../../../utils/hooks/query'
import { useHistory } from 'react-router-dom'
import { indexBy } from '../../../utils'
import { isLoggable } from '../../../utils/types'

type ExtendedTransactionListItem = TransactionListItem & {
  admin?: {
    id: string
    email: string
  }
}

export const Container: React.FunctionComponent = () => {
  const query = useQuery()
  const history = useHistory()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [response, setResponse] = useState<ExtendedTransactionListItem[]>([])
  const [companyId, setCompanyId] = useState<string | undefined>(
    query.get('company') as string
  )

  const [
    getTransactions,
    { response: transactionsResponse },
  ] = useTransactions()
  const [getEvents] = useEvents()

  const [request, setRequest] = useState<TransactionsRequest>({
    companyId: companyId,
    createdAtFrom: undefined,
    createdAtTo: undefined,
    ...defaultPagination,
  })

  useEffect(() => {
    setIsLoading(true)
    setResponse([])

    getTransactions(request)
      .then(({ data: transactions }) => {
        const referenceIds: string[] = loggableTransactionsReferences(
          transactions
        )
        getEvents({
          referenceId: referenceIds,
          page: request.page,
          perPage: request.perPage,
        }).then(({ data: events }) => {
          const eventsHashmap: Record<string, EventListItem> = indexBy<
            EventListItem
          >(events, 'referenceId')
          setResponse(
            transactions.map((transaction) => {
              return {
                ...transaction,
                ...{ admin: eventsHashmap[transaction.reference.id]?.admin },
              }
            })
          )
        })
      })
      .finally(() => setIsLoading(false))
  }, [getEvents, getTransactions, request])

  const onSubmit = (form: TransactionsRequest) => {
    if (request.companyId !== companyId) {
      companyId ? query.set('company', companyId) : query.delete('company')
      history.push({ search: '?' + query.toString() })
    }

    setRequest({
      ...request,
      ...form,
      companyId: companyId,
      ...defaultPagination,
    })
  }

  const onCompanyChange = (companyId?: string): void => {
    setCompanyId(companyId)
  }

  const onPaginationChange = (page: number, perPage?: number) => {
    const newRequest = Object.assign({}, request, {
      page: page,
      perPage: perPage || defaultPagination.perPage,
    })

    setRequest(newRequest)
  }

  const loggableTransactionsReferences = (
    transactions: TransactionListItem[]
  ): string[] => {
    return transactions
      .filter((t) => isLoggable(t.referenceType))
      .map((t) => t.reference.id)
  }

  return (
    <View
      transactions={response}
      meta={transactionsResponse?.meta}
      isLoading={isLoading}
      initialValues={request}
      onSubmit={onSubmit}
      companyId={companyId}
      onCompanyChange={onCompanyChange}
      onPaginationChange={onPaginationChange}
    />
  )
}
